import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import HowItWorks from '../content/HowItWorks';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import BbqFaq from '../faqs/BbqFaq';
import CateringCta from '../content/CateringCta';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function BbqCatering() {
  const { vehicle_type, site_title, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'bbq' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`BBQ Food ${vehicle_type} Catering in ${city_name}`}
        type='website'
        canonical='/food-truck-catering/bbq-catering'
        description={`Book BBQ food ${vehicleType} catering in ${city_name} for your next event. Smoked brisket, ribs, pulled pork & more. Get free quotes from top BBQ ${vehicleType}s.`}
        image='https://static.seattlefoodtruck.com/photos/bbq1.webp'
        structuredData={{
          cateringService: {
            name: `BBQ Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book BBQ food ${vehicleType} catering in ${city_name}. Smoked brisket, ribs, pulled pork and more.`,
            url: '/food-truck-catering/bbq-catering',
            serviceType: 'BBQ Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'BBQ Catering', item: '/food-truck-catering/bbq-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bbq1.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>BBQ Food {vehicle_type} Catering for Your Next Event</h1>
            <p>Nothing brings people together like the smoky, savory goodness of barbecue. Whether you&#39;re planning a <Link to='/food-truck-catering/corporate-catering'>corporate event</Link>, <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, our network of top-rated BBQ food {vehicleType}s will serve up mouthwatering brisket, ribs, pulled pork, and more. From Texas-style slow-smoked meats to Carolina pulled pork and Kansas City burnt ends, {site_title} makes it effortless to find and book the best BBQ {vehicleType}s in town. Simply tell us about your event, receive tailored quotes, and secure your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your BBQ Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='BBQ'
        image='https://static.seattlefoodtruck.com/photos/bbq2.webp'
        optionsText='Choose from Texas-style brisket, Memphis ribs, Carolina pulled pork, and more.'
      />

      <FeaturedTrucks title={`Featured BBQ Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a BBQ Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>The Best BBQ Food {vehicle_type}s in {city_name}</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bbq3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Authentic BBQ, Cooked Low and Slow</h4>
            <p>
              {site_title} partners with the best BBQ {vehicleType}s in the city, offering a variety of styles and techniques. From hickory-smoked brisket to tangy Carolina pulled pork, our vendors take their time to ensure the highest-quality flavors. Many use traditional wood smokers, house-made spice rubs, and secret sauce recipes passed down through generations. Whether you&#39;re a fan of dry rubs or sticky, finger-licking barbecue sauce, we have the perfect truck for your event.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bbq4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Effortless BBQ Catering for Any Event</h4>
            <p>
              Booking a BBQ {vehicleType} for your event should be as easy as enjoying a plate of ribs. Our streamlined process allows you to enter your event details, receive multiple quotes from verified BBQ vendors, and book in minutes. Whether you&#39;re planning a casual backyard get-together or a large-scale event, our team ensures that every detail is handled with care.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bbq5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Dedicated Support and Customization</h4>
            <p>
              We go beyond simple bookings — we help craft an experience. Need a whole hog roast? Want a selection of regional BBQ styles? Need vegetarian options alongside the smoked meats? We work closely with our BBQ food {vehicleType}s to accommodate your event&#39;s needs, ensuring a menu that suits every guest. And if something unexpected happens, we have backup vendors ready to step in.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks
        text1='Tell us about your event, location, and BBQ preferences.'
        text2={`Get customized offers from available BBQ food ${vehicleType}s that fit your budget.`}
        text3='Compare quotes, message vendors directly, and secure your booking.'
      />

      <AnyEvent />

      <Testimonials title="Join Thousands Who've Booked BBQ Catering With Us!" />

      <CostEstimator title='BBQ Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <BbqFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default BbqCatering;
